import {
  LIVE_BETTING_REFRESH_INTERVAL,
  LIVE_SPORT_IDs,
} from 'config/constants';
import { UserContext } from 'context/userContext';
import { PROGRESSIVE_FIRST } from 'hooks/useProgressiveCount';
import { deepCopy } from 'helpers/dataHelper';
import { deepCopyMap, getTotalEelementsCount } from 'helpers/mapHelper';
import React, {
  startTransition,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { getLiveCounts, getLiveEvents } from 'services/api/sportBetsService';
import {
  subscribeLiveSports,
  createWebSocket,
  unsubscribeLiveSports,
} from 'services/webSocketService';
import { LiveEvent } from 'types/LiveEvent';
import { hasLiveEventStarted, mapLiveEvent } from 'utils/sportEvent.utils';

export const LiveBettingContext = React.createContext(null as any);

interface Props {
  children: React.ReactNode;
}
// Live favourites are kept on the device, so they are there again on the
// next visit until their matches end (see the sync with the feed below).
/** A live row's score and clock, laid over by the newer reading. */
const BOARD_KEYS = ['SS', 'TT', 'TIME', 'CLK_AT'] as const;

const FAVORITES_KEY = 'live-favorites';
// A favourite missing from the feed this long has ended.
const FAVORITE_GONE_AFTER_MS = 90_000;
// Saved longer ago than this, every match on the list is over.
const FAVORITES_MAX_AGE_MS = 6 * 60 * 60 * 1000;

/** The saved favourites. `savedAt` is when the feed last had them: a list
 *  that old is not shown again until the feed confirms each match, because a
 *  match from last night must not come back on this morning's page. */
const loadFavorites = () => {
  const empty = new Map<number, LiveEvent[]>();
  try {
    const saved = JSON.parse(localStorage.getItem(FAVORITES_KEY) ?? 'null');
    const age = Date.now() - Number(saved?.savedAt);
    if (Array.isArray(saved?.list) && age < FAVORITES_MAX_AGE_MS) {
      const list = new Map<number, LiveEvent[]>(saved.list);
      return age < FAVORITE_GONE_AFTER_MS
        ? { shown: list, unconfirmed: empty }
        : { shown: empty, unconfirmed: list };
    }
  } catch {
    // Nothing saved, or storage blocked.
  }
  return { shown: empty, unconfirmed: empty };
};

// What was on screen when the page closed, so a refresh shows it at once and
// the fresh lists arrive over it. Only for a quick refresh: old prices are
// not left on a page for long.
const SNAPSHOT_KEY = 'live-snapshot';
const SNAPSHOT_MAX_AGE_MS = 60_000;
const SNAPSHOT_SPORTS = 2;

const loadSnapshot = (): Map<number, LiveEvent[]> => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(SNAPSHOT_KEY) ?? 'null');
    if (
      Array.isArray(saved?.list) &&
      Date.now() - Number(saved.savedAt) < SNAPSHOT_MAX_AGE_MS
    ) {
      return new Map(saved.list);
    }
  } catch {
    // Nothing saved, or storage blocked.
  }
  return new Map();
};

// The rail's numbers as the lists last counted them. The feed's own count
// also holds matches that have not begun, so a page opened on it would jump
// from 309 to 281 as the lists arrive.
const COUNTS_KEY = 'live-counts';
const COUNTS_MAX_AGE_MS = 30 * 60 * 1000;

const loadSavedCounts = (): Map<number, number> => {
  try {
    const saved = JSON.parse(localStorage.getItem(COUNTS_KEY) ?? 'null');
    if (
      saved &&
      Array.isArray(saved.list) &&
      Date.now() - Number(saved.savedAt) < COUNTS_MAX_AGE_MS
    ) {
      return new Map(saved.list);
    }
  } catch {
    // Nothing saved, or storage blocked.
  }
  return new Map();
};

/** The feed says which sports are on; what we counted last stays the number. */
const mergeCounts = (
  known: Map<number, number>,
  feed: Map<number, number>
): Map<number, number> =>
  new Map(
    Array.from(feed).map(([id, n]) => [id, Math.min(known.get(id) ?? n, n)])
  );

// A sport nobody has open is refreshed this often, one at a time.
const BACKGROUND_SPORT_REFRESH_MS = 30_000;
// A breath between background sports, so the page can answer the player.
const BACKGROUND_SPORT_PAUSE_MS = 150;

const pause = (ms: number) =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

const LiveBettingProvider = ({ children }: Props) => {
  const { webSocket } = useContext(UserContext);
  const [guestWebSocket, setGuestWebSocket] = useState<WebSocket | null>(null);
  const [events, setEvents] = useState<Map<number, LiveEvent[]>>(new Map());
  const [prevData, setPrevData] = useState<Map<number, LiveEvent[]>>(new Map());
  const [restored] = useState(loadFavorites);
  const [favorites, setFavorites] = useState(restored.shown);
  // Saved favourites waiting for their sport's list (see loadFavorites).
  const unconfirmedFavorites = useRef(new Map(restored.unconfirmed));
  const [snapshot] = useState(loadSnapshot);
  // When each favourite was first missing from a loaded feed (fi -> ms).
  const favoriteMissingSince = useRef<Map<string, number>>(new Map());
  const [firstFetch, setFirstFetch] = useState(true);
  const [wsLiveEnabled, setWsLiveEnabled] = useState(false);
  // Numbers for the sport rail, there before any list is.
  const [liveCounts, setLiveCounts] =
    useState<Map<number, number>>(loadSavedCounts);
  // False until the first list has answered: the page shows its outline.
  const [liveReady, setLiveReady] = useState(false);
  // The sports the page is showing; null = all of them (the desktop table).
  // Only these take live updates: a phone looking at tennis has no use for
  // 3 MB of football every few seconds.
  const [watchedSports, setWatchedSports] = useState<number[] | null>(null);
  const lastLiveVersionRef = useRef<Map<number, number>>(new Map());
  // Each match's last feed row, for the fast scores to be laid over.
  const rawByFi = useRef<Map<string, any>>(new Map());
  // The score and clock travel in two messages (the lists and the fast
  // scores) with no order between them: whichever reading is newer (CLK_AT,
  // when the clock was read) stays, so an older list cannot put an older
  // clock back (2026-11-04: half time shown again after the restart).
  const rememberRaw = (row: any) => {
    if (row?.FI == null) return row;
    const fi = String(row.FI);
    const known = rawByFi.current.get(fi);
    let next = row;
    if (Number(known?.CLK_AT) > Number(row.CLK_AT ?? 0)) {
      next = { ...row };
      BOARD_KEYS.forEach((k) => {
        if (known[k] !== undefined) next[k] = known[k];
      });
    }
    rawByFi.current.set(fi, next);
    return next;
  };

  /**
   * The sync's fast loop (about once a second): new scores and clocks of a
   * sport's matches, laid over their rows. Only those matches get a new
   * object, and their markets keep theirs, so the rest of the list and the
   * prices are not drawn again (owner, 2026-11-03: goals 10-25 s late).
   */
  const applyLiveScores = (
    sportId: number,
    changes: {
      fi: string | number;
      SS?: string;
      TIME?: string;
      TT?: string;
      CLK_AT?: number;
    }[]
  ) => {
    const fresh = new Map<string, LiveEvent>();
    changes.forEach((c) => {
      const fi = String(c.fi);
      const raw = rawByFi.current.get(fi);
      if (!raw) return;
      // An older reading than the row already has changes nothing.
      if (c.CLK_AT && Number(raw.CLK_AT) > c.CLK_AT) return;
      const next = { ...raw };
      if (c.SS) next.SS = c.SS;
      if (c.TIME) next.TIME = c.TIME;
      if (c.TT) next.TT = c.TT;
      if (c.CLK_AT) next.CLK_AT = c.CLK_AT;
      rawByFi.current.set(fi, next);
      const mapped = mapLiveEvent(next, sportId);
      if (mapped) fresh.set(fi, mapped);
    });
    if (!fresh.size) return;
    setEvents((prev) => {
      const list = prev.get(sportId);
      if (!list) return prev;
      let touched = false;
      const nextList = list.map((ev) => {
        const m = fresh.get(String(ev.fi));
        if (!m) return ev;
        touched = true;
        // The board only (score, clock, sets); the markets stay as they are.
        const board: Partial<LiveEvent> = { ...m };
        delete board.bets;
        delete board.fullTimeBet;
        return { ...ev, ...board } as LiveEvent;
      });
      return touched ? new Map(prev).set(sportId, nextList) : prev;
    });
  };
  const loadedCount = getTotalEelementsCount(events);
  // While the lists load, the header counts what the rail counts.
  const liveEventsCount =
    events.size < liveCounts.size
      ? Array.from(liveCounts.entries()).reduce(
          (sum, [id, n]) => sum + (events.get(id)?.length ?? n),
          0
        )
      : loadedCount;
  const favoritesCount = getTotalEelementsCount(favorites);

  const watchedSportsKey = watchedSports?.join(',') ?? 'all';
  const currentLocation = useLocation().pathname;
  const activeWebSocket = webSocket ?? guestWebSocket;

  useEffect(() => {
    if (currentLocation !== '/live-betting' || webSocket || guestWebSocket) {
      return;
    }
    const socket = createWebSocket();
    setGuestWebSocket(socket);
  }, [currentLocation, webSocket, guestWebSocket]);

  useEffect(() => {
    // Tear down guest socket when leaving live page or when authenticated socket is available.
    if (currentLocation === '/live-betting' && !webSocket) {
      return;
    }
    if (guestWebSocket) {
      guestWebSocket.close();
      setGuestWebSocket(null);
    }
  }, [currentLocation, webSocket, guestWebSocket]);

  useEffect(() => {
    // Favourites follow the feed: fresh data while the match is on offer,
    // and gone once it has left it (ended). Nothing is dropped before the
    // feed has loaded, and a match missing from one update is kept a while.
    if (events.size === 0) return;
    const now = Date.now();
    const missing = favoriteMissingSince.current;
    const confirmed = new Map<number, LiveEvent[]>();
    unconfirmedFavorites.current.forEach((sportEvents, sportId) => {
      const rows = events.get(sportId);
      if (!rows) return;
      unconfirmedFavorites.current.delete(sportId);
      const alive = rows.filter((y) => sportEvents.some((x) => x.fi === y.fi));
      if (alive.length > 0) confirmed.set(sportId, alive);
    });
    setFavorites((prevFavorites) => {
      if (prevFavorites.size === 0 && confirmed.size === 0) {
        return prevFavorites;
      }
      const newFavorites = new Map(confirmed);
      prevFavorites.forEach((sportEvents, sportId) => {
        const realEventsData = events.get(sportId) ?? [];
        const kept: LiveEvent[] = [];
        sportEvents.forEach((x) => {
          const eventData = realEventsData.find((y) => y.fi === x.fi);
          if (eventData) {
            missing.delete(x.fi);
            kept.push(deepCopy(eventData) as LiveEvent);
            return;
          }
          const since = missing.get(x.fi) ?? now;
          missing.set(x.fi, since);
          if (now - since < FAVORITE_GONE_AFTER_MS) {
            kept.push(x);
          } else {
            missing.delete(x.fi);
          }
        });
        if (kept.length > 0) {
          newFavorites.set(sportId, [
            ...(newFavorites.get(sportId) ?? []).filter(
              (x) => !kept.some((y) => y.fi === x.fi)
            ),
            ...kept,
          ]);
        }
      });
      return newFavorites;
    });
  }, [events]);

  useEffect(() => {
    if (currentLocation !== '/live-betting' || !activeWebSocket) {
      setWsLiveEnabled(false);
      return;
    }

    const sports = watchedSports ?? LIVE_SPORT_IDs;
    const subscribe = () => {
      subscribeLiveSports(activeWebSocket, sports);
      setWsLiveEnabled(true);
    };
    const unsubscribe = () => {
      unsubscribeLiveSports(activeWebSocket, sports);
      setWsLiveEnabled(false);
    };

    if (activeWebSocket.readyState === WebSocket.OPEN) {
      subscribe();
    } else {
      activeWebSocket.addEventListener('open', subscribe);
    }

    const onMessage = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'live_scores' && typeof data.sport_id === 'number') {
          applyLiveScores(data.sport_id, data.events ?? []);
          return;
        }
        if (data.type !== 'live_update' || typeof data.sport_id !== 'number') {
          return;
        }

        const version = Number(data.version ?? 0);
        const lastVersion =
          lastLiveVersionRef.current.get(data.sport_id) ??
          Number.NEGATIVE_INFINITY;
        if (version <= lastVersion) {
          return;
        }
        lastLiveVersionRef.current.set(data.sport_id, version);

        const mappedEvents = (data.events ?? [])
          .filter((x: any) => hasLiveEventStarted(x.results?.[0]))
          .map((x: any) => rememberRaw(x.results?.[0]))
          .map((x: any) => mapLiveEvent(x, data.sport_id))
          .filter((x: any) => !!x);

        // A transition: a tap or a scroll goes first, the new prices after.
        startTransition(() => {
          setEvents((prevEvents) => {
            const oldList = prevEvents.get(data.sport_id);
            setPrevData((prevPrev) => {
              // Lists are replaced, never changed, so no copying.
              const base = new Map(prevPrev.size > 0 ? prevPrev : prevEvents);
              if (oldList !== undefined) base.set(data.sport_id, oldList);
              return base;
            });
            return new Map(prevEvents).set(data.sport_id, mappedEvents);
          });
        });
      } catch {
        // Ignore malformed websocket payloads and keep HTTP fallback active.
      }
    };

    activeWebSocket.addEventListener('message', onMessage);

    return () => {
      activeWebSocket.removeEventListener('message', onMessage);
      activeWebSocket.removeEventListener('open', subscribe);
      if (activeWebSocket.readyState === WebSocket.OPEN) {
        unsubscribe();
      }
    };
  }, [activeWebSocket, currentLocation, watchedSportsKey]);

  // Opening the page: the rail's numbers, then the sport in front (football
  // first), the page drawn from that; the others behind it, one at a time.
  useEffect(() => {
    if (currentLocation !== '/live-betting') {
      setFirstFetch(true);
      return;
    }
    if (!firstFetch) return;
    setFirstFetch(false);
    let stopped = false;
    loadedSports.current.clear();
    (async () => {
      getLiveCounts(LIVE_SPORT_IDs).then((counts) => {
        if (!stopped) setLiveCounts((known) => mergeCounts(known, counts));
      });
      await loadSport(watchedSports?.[0] ?? LIVE_SPORT_IDs[0]);
      setLiveReady(true);
      for (const id of LIVE_SPORT_IDs) {
        if (stopped) return;
        if (loadedSports.current.has(id)) continue;
        await pause(BACKGROUND_SPORT_PAUSE_MS);
        await loadSport(id, true);
      }
    })();
    return () => {
      stopped = true;
      setFirstFetch(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation]);

  // A sport picked on the rail that has not loaded yet comes now.
  useEffect(() => {
    if (currentLocation !== '/live-betting') return;
    watchedSports?.forEach((id) => {
      if (!loadedSports.current.has(id)) loadSport(id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedSportsKey, currentLocation]);

  // Without updates (socket down) the shown sports are fetched; the sports
  // that take no updates stay fresh in the background.
  useEffect(() => {
    if (currentLocation !== '/live-betting') return;
    let stopped = false;
    let timer: number;
    const round = async () => {
      const shown = watchedSports ?? LIVE_SPORT_IDs;
      if (!wsLiveEnabled) {
        for (const id of shown) {
          if (stopped) return;
          await loadSport(id, true);
        }
      }
      if (stopped) return;
      timer = window.setTimeout(round, LIVE_BETTING_REFRESH_INTERVAL);
    };
    timer = window.setTimeout(round, LIVE_BETTING_REFRESH_INTERVAL);
    const refreshOthers = window.setInterval(async () => {
      const counts = await getLiveCounts(LIVE_SPORT_IDs);
      if (stopped) return;
      setLiveCounts((known) => mergeCounts(known, counts));
      if (!watchedSports || document.hidden) return;
      for (const id of Array.from(counts.keys())) {
        if (stopped) return;
        if (watchedSports.includes(id)) continue;
        await pause(BACKGROUND_SPORT_PAUSE_MS);
        await loadSport(id, true);
      }
    }, BACKGROUND_SPORT_REFRESH_MS);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      window.clearInterval(refreshOthers);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation, wsLiveEnabled, watchedSportsKey]);

  useEffect(() => {
    if (events.size === 0) return;
    try {
      const list = new Map(liveCounts);
      events.forEach((rows, id) => list.set(id, rows.length));
      localStorage.setItem(
        COUNTS_KEY,
        JSON.stringify({ savedAt: Date.now(), list: Array.from(list) })
      );
    } catch {
      // Storage blocked: the rail starts on the feed's numbers.
    }
  }, [events, liveCounts]);

  useEffect(() => {
    // Opening the app changes nothing about them: saving now would make last
    // night's list look fresh, and it would never age out.
    if (favorites === restored.shown) return;
    try {
      localStorage.setItem(
        FAVORITES_KEY,
        JSON.stringify({ savedAt: Date.now(), list: Array.from(favorites) })
      );
    } catch {
      // Storage blocked (private mode): favourites last this visit only.
    }
  }, [favorites, restored]);

  const latest = useRef({ events, watchedSports });
  latest.current = { events, watchedSports };
  useEffect(() => {
    const save = () => {
      if (window.location.pathname !== '/live-betting') return;
      const { events: rows, watchedSports: sports } = latest.current;
      const list = (sports ?? LIVE_SPORT_IDs)
        .filter((id) => rows.get(id)?.length)
        .slice(0, SNAPSHOT_SPORTS)
        .map((id) => [id, rows.get(id)!.slice(0, PROGRESSIVE_FIRST)]);
      if (list.length === 0) return;
      try {
        sessionStorage.setItem(
          SNAPSHOT_KEY,
          JSON.stringify({ savedAt: Date.now(), list })
        );
      } catch {
        // Storage blocked or full: the page opens on its outline.
      }
    };
    window.addEventListener('pagehide', save);
    return () => window.removeEventListener('pagehide', save);
  }, []);

  const getLastOddsForEvent = (event: LiveEvent) => {
    const prev = prevData
      .get(event.sportId)
      ?.filter((x) => x.fi === event.fi)[0];
    return prev;
  };

  const addFavorite = (event: LiveEvent) => {
    const newFavoritesMap =
      favorites.size > 0 ? deepCopyMap(favorites) : new Map();

    if (newFavoritesMap.has(event.sportId)) {
      newFavoritesMap.get(event.sportId).push(event);
    } else {
      newFavoritesMap.set(event.sportId, [event]);
    }

    setFavorites(newFavoritesMap);
  };

  const removeFavorite = (event: LiveEvent) => {
    const newFavoritesMap = deepCopyMap(favorites);

    const eventsArray: LiveEvent[] = newFavoritesMap.get(event.sportId);

    const indexToDelete = eventsArray.findIndex((x) => x.fi === event.fi);
    if (indexToDelete > -1) {
      eventsArray.splice(indexToDelete, 1);
      setFavorites(newFavoritesMap);
    }
  };

  const isFavorite = (event: LiveEvent) => {
    const eventArr = favorites.get(event.sportId);
    let isFav = false;
    if (eventArr) {
      isFav = eventArr.findIndex((x) => x.fi === event.fi) > -1;
    }
    return isFav;
  };

  const loadedSports = useRef<Set<number>>(new Set());
  // Asked and not answered yet: 3 MB of football is not fetched twice.
  const loadingSports = useRef<Set<number>>(new Set());

  /** One sport's list, into the page when it comes; the rest wait for no one. */
  const loadSport = async (sportId: number, background = false) => {
    if (loadingSports.current.has(sportId)) return;
    loadingSports.current.add(sportId);
    const rows = await getLiveEvents(sportId);
    loadingSports.current.delete(sportId);
    if (!Array.isArray(rows)) {
      // Failed: its place is given up, so the sports after it still show.
      // Asked again when the player opens it; an update fills it as well.
      setEvents((prev) =>
        prev.has(sportId) ? prev : new Map(prev).set(sportId, [])
      );
      return;
    }
    const mapped = rows
      .filter((x: any) => hasLiveEventStarted(x.results[0]))
      .map((x: any) => mapLiveEvent(rememberRaw(x.results[0]), sportId))
      .filter((x: any) => !!x) as LiveEvent[];
    loadedSports.current.add(sportId);
    const apply = () =>
      setEvents((prev) => {
        const old = prev.get(sportId);
        if (old !== undefined) {
          setPrevData((p) => new Map(p).set(sportId, old));
        }
        return new Map(prev).set(sportId, mapped);
      });
    if (background) startTransition(apply);
    else apply();
  };

  const contextValue = useMemo(
    () => ({ setWatchedSports }),
    [setWatchedSports]
  );

  return (
    <LiveBettingContext.Provider
      value={{
        ...contextValue,
        events,
        snapshot,
        liveCounts,
        liveReady,
        favorites,
        prevData,
        liveEventsCount,
        favoritesCount,
        addFavorite,
        removeFavorite,
        isFavorite,
        getLastOddsForEvent,
      }}
    >
      {children}
    </LiveBettingContext.Provider>
  );
};

export default LiveBettingProvider;
